import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../ui/card';
import { ShoppingCart, X, Truck, Minus, Plus, ChevronRight } from 'lucide-react';
import { CATEGORY_LABELS, PRICING_MODEL_LABELS } from '../../utils/planningConstants';

export default function CartSidebar({ hub }) {
  const { cart, eventDetails, removeFromCart, updateQuantity, updateItemGuestCount, updateItemHours,
    toggleItemDropoff, getSelectedOptionNames, quote, setShowReview } = hub;

  if (cart.length === 0) return (
    <div className="w-80 flex-shrink-0 hidden lg:block">
      <Card className="sticky top-24" data-testid="cart-sidebar">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2"><ShoppingCart className="h-5 w-5" />Your Event</CardTitle>
        </CardHeader>
        <CardContent><p className="text-sm text-muted-foreground text-center py-8">Add services to start building your event</p></CardContent>
      </Card>
    </div>
  );

  const grouped = cart.reduce((g, item) => { const c = item.category || 'other'; if (!g[c]) g[c] = []; g[c].push(item); return g; }, {});

  return (
    <div className="w-80 flex-shrink-0 hidden lg:block">
      <Card className="sticky top-24" data-testid="cart-sidebar">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2"><ShoppingCart className="h-5 w-5" />Your Event<Badge className="ml-auto">{cart.length}</Badge></CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 max-h-[40vh] overflow-y-auto">
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat}>
              <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">{CATEGORY_LABELS[cat] || cat}</p>
              {items.map(item => {
                const optNames = getSelectedOptionNames(item);
                return (
                  <div key={item.service_id} className={`flex items-start gap-2 p-2 rounded-lg mb-2 border ${item.isDropoff ? 'border-emerald-300 bg-emerald-50/50 dark:bg-emerald-950/20' : 'border-border'}`} data-testid={`cart-item-${item.service_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium truncate">{item.name}</p>
                        {item.isDropoff && <Badge className="bg-emerald-500 text-[10px] px-1"><Truck className="h-2.5 w-2.5" /></Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground">{PRICING_MODEL_LABELS[item.pricing_model]}</p>
                      {item.has_dropoff_option && (
                        <button onClick={() => toggleItemDropoff(item.service_id)} className="text-[10px] text-indigo-500 hover:underline mt-0.5" data-testid={`toggle-dropoff-${item.service_id}`}>
                          Switch to {item.isDropoff ? 'Full Service' : 'Drop-off'}
                        </button>
                      )}
                      {!item.isDropoff && optNames.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {optNames.map((o, i) => <Badge key={i} variant="secondary" className="text-[9px]">{o.choiceName}</Badge>)}
                        </div>
                      )}
                      {!item.isDropoff && (
                        <div className="flex items-center gap-2 mt-1.5">
                          {item.isDropoff ? (
                            <div className="flex items-center gap-1">
                              <button onClick={() => updateQuantity(item.service_id, -1)} className="w-5 h-5 rounded bg-muted flex items-center justify-center text-xs"><Minus className="h-3 w-3" /></button>
                              <span className="text-xs w-5 text-center">{item.quantity}</span>
                              <button onClick={() => updateQuantity(item.service_id, 1)} className="w-5 h-5 rounded bg-muted flex items-center justify-center text-xs"><Plus className="h-3 w-3" /></button>
                            </div>
                          ) : (item.pricing_model === 'per_person' || item.pricing_model === 'per_hour_per_person') ? (
                            <div className="flex items-center gap-1">
                              <button onClick={() => updateItemGuestCount(item.service_id, -5)} className="w-5 h-5 rounded bg-muted flex items-center justify-center text-xs"><Minus className="h-3 w-3" /></button>
                              <span className="text-xs w-8 text-center">{item.guestCount || eventDetails.guestCount}pp</span>
                              <button onClick={() => updateItemGuestCount(item.service_id, 5)} className="w-5 h-5 rounded bg-muted flex items-center justify-center text-xs"><Plus className="h-3 w-3" /></button>
                            </div>
                          ) : (item.pricing_model === 'per_hour') ? (
                            <div className="flex items-center gap-1">
                              <button onClick={() => updateItemHours(item.service_id, -1)} className="w-5 h-5 rounded bg-muted flex items-center justify-center text-xs"><Minus className="h-3 w-3" /></button>
                              <span className="text-xs w-8 text-center">{item.hours || eventDetails.duration}hr</span>
                              <button onClick={() => updateItemHours(item.service_id, 1)} className="w-5 h-5 rounded bg-muted flex items-center justify-center text-xs"><Plus className="h-3 w-3" /></button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              <button onClick={() => updateQuantity(item.service_id, -1)} className="w-5 h-5 rounded bg-muted flex items-center justify-center text-xs"><Minus className="h-3 w-3" /></button>
                              <span className="text-xs w-5 text-center">{item.quantity}</span>
                              <button onClick={() => updateQuantity(item.service_id, 1)} className="w-5 h-5 rounded bg-muted flex items-center justify-center text-xs"><Plus className="h-3 w-3" /></button>
                            </div>
                          )}
                        </div>
                      )}
                      {item.isDropoff && (
                        <div className="flex items-center gap-1 mt-1.5">
                          <button onClick={() => updateQuantity(item.service_id, -1)} className="w-5 h-5 rounded bg-muted flex items-center justify-center text-xs"><Minus className="h-3 w-3" /></button>
                          <span className="text-xs w-5 text-center">{item.quantity}</span>
                          <button onClick={() => updateQuantity(item.service_id, 1)} className="w-5 h-5 rounded bg-muted flex items-center justify-center text-xs"><Plus className="h-3 w-3" /></button>
                        </div>
                      )}
                    </div>
                    <button onClick={() => removeFromCart(item.service_id)} className="text-muted-foreground hover:text-destructive p-1" data-testid={`remove-item-${item.service_id}`}>
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </CardContent>
        {quote && quote.subtotal !== undefined && (
          <CardFooter className="flex-col gap-2 border-t pt-4">
            <div className="w-full space-y-1.5">
              <div className="flex justify-between text-sm"><span>Subtotal</span><span>${(quote.subtotal || 0).toFixed(2)}</span></div>
              {(quote.admin_service_fee || 0) > 0 && (
                <div className="flex justify-between text-sm">
                  <span>Service Fee <span className="text-xs text-muted-foreground">(F&B)</span></span>
                  <span>${(quote.admin_service_fee || 0).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm"><span>Tax ({quote.tax_rate || 0}%)</span><span>${(quote.tax || quote.tax_amount || 0).toFixed(2)}</span></div>
              <div className="flex justify-between font-bold text-lg border-t pt-2"><span>Total</span><span>${(quote.total || 0).toFixed(2)}</span></div>
              <div className="flex justify-between text-sm text-indigo-600 dark:text-indigo-400">
                <span>50% Deposit</span><span className="font-semibold">${(quote.deposit_amount || 0).toFixed(2)}</span>
              </div>
            </div>
            <Button className="w-full mt-4" size="lg" onClick={() => setShowReview(true)} data-testid="proceed-to-review">Continue to Review</Button>
          </CardFooter>
        )}
      </Card>
    </div>
  );
}
